import { useQuery } from "@tanstack/react-query";
import { fetchDirectusMatchById } from "../api/matches";
import {
  HookOptions,
  useStatsQuery,
} from "../extensions/sw/modules/stats/hooks";
import { StatsQuery } from "../extensions/sw/modules/stats/types";
import { MatchFilter } from "../libs/statsFilters";
import { WorkerQueryTransferModes } from "../libs/workers/consts";
import { WorkerQueryOptions } from "../libs/workers/types";
import { useIndexedDb } from "../providers";
import { useDirectus } from "../providers/DirectusProvider";
import { GlobalStatsSelection } from "../types/stats";

export function useDirectusMatch(
  matchId: string,
  options?: { enabled: boolean }
) {
  const { client } = useDirectus();
  return useQuery({
    queryKey: ["matches", "directus", matchId],
    queryFn: () => fetchDirectusMatchById(client, matchId),
    enabled: !!matchId && (options?.enabled ?? true),
  });
}

export function useIndexedDbMatches(options?: { enabled: boolean }) {
  const { getDb } = useIndexedDb();

  return useQuery({
    queryKey: ["matches", "indexedDb"],
    queryFn: async () => {
      console.debug("Fetching matches from IndexedDB");
      const db = await getDb();
      try {
        const result = await db.getAll("matches");
        console.debug(`Fetched ${result.length} matches from IndexedDB`);
        console.log("Matches data:", result);
        return result;
      } finally {
        db.close();
      }
    },
  });
}

export function useAllMatches(
  data: {
    selection?: GlobalStatsSelection & { enigmaId?: string };
    filters?: MatchFilter;
    opts?: WorkerQueryOptions;
  },
  opts?: HookOptions<typeof StatsQuery.ALL_MATCHES>
) {
  return useStatsQuery(
    {
      type: StatsQuery.ALL_MATCHES,
      data: {
        cityEscapeIds: data.selection?.cityEscapeIds,
        productIds: data.selection?.productIds,
        enigmaId: data.selection?.enigmaId,
        filters: data.filters,
      },
      opts: {
        ...data?.opts,
        response: {
          ...(data?.opts?.response ?? {}),
          useCompression: false,
          transferMode: WorkerQueryTransferModes.ARRAY_BUFFER,
        },
        traceKey: data.opts?.traceKey ?? "useAllMatches",
      },
    },
    opts
  );
}

export function useAllMatchesIds(
  props: {
    cityEscapeId?: string;
    enigmaId?: string;
    productIds?: string | string[] | number | number[] | null;
    filters?: MatchFilter;
  },
  opts?: WorkerQueryOptions
) {
  return useStatsQuery({
    type: StatsQuery.ALL_MATCHES_IDS,
    data: {
      cityEscapeIds: props.cityEscapeId,
      productIds: props.productIds,
      enigmaId: props.enigmaId,
      filters: props.filters,
    },
    opts: { ...opts, traceKey: opts?.traceKey ?? "useAllMatchesIds" },
  });
}

export function useMatchesCityEscapeSummaries(
  props: { cityEscapeIds?: string | string[] | null; filters?: MatchFilter },
  opts?: WorkerQueryOptions
) {
  return useStatsQuery({
    type: StatsQuery.MATCHES_CITY_ESCAPE_SUMMARIES,
    data: props,
    opts: {
      ...opts,
      traceKey: opts?.traceKey ?? "useMatchesCityEscapeSummaries",
    },
  });
}
